import { Component } from '../components/core/component.js';
import { ComponentList } from '../components/core/component-list.js';
import { IEntity } from '../components/core/entity.js';
import { DataModel } from './dto/dto.js';
import { BusinessModuleRef } from './serializable/business-module-ref.js';
import { PhoneNumber } from './serializable/phone-number.js';
import { PhotoReference } from './serializable/file-reference/photo-reference.js';
import { MembershipRole } from './serializable/membership-role.js';
import { fromEnumAsString } from '../serialization/enum-utils.js';

export enum PartnerProfileStatus {
  Live = 'LIVE',
  Archived = 'ARCHIVED',
}

export abstract class PartnerProfileBase extends DataModel implements IEntity {
    abstract get uniqueId(): string;
    abstract get businessRef(): BusinessModuleRef;
    abstract get firstName(): string;
    abstract get lastName(): string;
    abstract get phoneNumber(): PhoneNumber;
    abstract get photo(): PhotoReference | null;
    abstract get userId(): string | null;
    abstract get pushNotificationToken(): string | null;
    abstract get partnerRole(): MembershipRole;
    abstract get status(): PartnerProfileStatus;
    abstract get email(): string | null;
    abstract get components(): ComponentList;

    static encodedStatusSafe(status: PartnerProfileStatus): string {
      return fromEnumAsString(status);
    }

    hasComponentByTypeId(typeId: string): boolean {
      return this.components.hasComponentByTypeId(typeId);
    }

    mustFindComponentByTypeId<C extends Component>(typeId: string): C {
      return this.components.mustFindComponentByTypeId<C>(typeId);
    }

    findComponentByTypeId<C extends Component>(typeId: string): C | null {
      return this.components.findComponentByTypeId<C>(typeId);
    }

    findComponentByInstanceId<C extends Component>(instanceId: string): C | null {
      return this.components.findComponentByInstanceId<C>(instanceId);
    }

    findComponentsByTypeId<C extends Component>(typeId: string): C[] {
      return this.components.findComponentsByTypeId<C>(typeId);
    }

    addComponent<C extends Component>(component: C): void {
      this.components.addComponent(component);
    }

    addComponents(list: Component[]): void {
      this.components.addComponents(list);
    }

    removeComponentByTypeId(typeId: string): boolean {
      return this.components.removeComponentByTypeId(typeId);
    }

    removeComponentByInstanceId(instanceId: string): boolean {
      return this.components.removeComponentByInstanceId(instanceId);
    }

    removeComponentAt(index: number): void {
      this.components.removeComponentAt(index);
    }

    removeAllComponents(): void {
      this.components.removeAllComponents();
    }

    popAllComponentsByTypeId<C extends Component>(typeId: string): C[] {
      return this.components.popAllComponentsByTypeId(typeId);
    }

    popComponentByTypeId<C extends Component>(typeId: string): C | null {
      return this.components.popComponentByTypeId(typeId);
    }

    popComponent<C extends Component>(component: C): C | null {
      return this.components.popComponent(component);
    }

    replaceComponent<C extends Component>(replacement: C): boolean {
      return this.components.replaceComponent(replacement);
    }

    addOrReplaceComponent<C extends Component>(component: C): void {
      this.components.addOrReplaceComponent(component);
    }

    mustFindComponentField<C extends Component, F>(componentTypeId: string, find: (c: C) => F): F {
      return this.components.mustFindComponentField<C, F>(componentTypeId, find);
    }

    findComponentField<C extends Component, F>(componentTypeId: string, find: (c: C) => F): F | null {
      return this.components.findComponentField<C, F>(componentTypeId, find);
    }
}
